import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { Fir5471ScheduleIService } from './fir-5471-schedule-i.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface FirOverrideValue {
  VALUE: number | string | null;
  line_attrib_key: string;
  attrib_name: string;
  is_editable: boolean;
  is_Display: boolean;
  column_style?: any;
}

interface GhostAmt {
  VALUE: number | null;
  line_attrib_key: string;
  attrib_name: string;
  is_editable: boolean;
  is_Display: boolean;
  column_style?: any;
}

interface ScheduleILineItem {
  line_no: string | number;
  line_Description: string;
  backgroudColor?: any;
  saveParams: {
    tax_year: string;
    trans_type_key: string;
    trans_Details_key: string;
    occurrence: string;
    form_key: string;
  };
  FIR_OVERRIDE_VALUE: FirOverrideValue;
  GHOST_AMT: GhostAmt;
}

interface GhostAmtItem {
  LINE_NO: number | string;
  ADJ_AMT: number | null;
  LINE_ATTRIB_KEY: string;
  FORM_KEY: string;
  OVERRIDE_Y_N: string | null;
  TRANS_DETAILS_KEY: string | null;
  TRANS_HEADER_KEY: string | null;
  TRANS_TYPE_KEY: number;
}

@Component({
    selector: 'gtw-fir-5471-schedule-i',
    templateUrl: './fir-5471-schedule-i.component.html',
    styleUrls: ['./fir-5471-schedule-i.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Fir5471ScheduleIComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private unsubscribe$ = new Subject<void>();
  private subscriptions = new Subscription();

  modalTitle = "Schedule I: Summary of Shareholder's Income From Foreign Corporation";
  dataLoading = false;
  crudLoading = false;
  userMessage = '';
  minimise = false;
  isInitialized = false;
  loadFlag = false;

  irsF5471ScheduleIData: ScheduleILineItem[] = [];
  irsF5471ScheduleIOriginalData: ScheduleILineItem[] = [];
  GHOSTAmt: GhostAmtItem[] = [];

  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private fir5471ScheduleIService: Fir5471ScheduleIService,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndTryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    this.filterParams = fp;
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    if (Object.keys(rowObj).length) {
      this.rowData = rowObj;
    }
  }

  private resetAndTryInit(): void {
    this.isInitialized = false;
    this.tryInit();
    this.cdr.markForCheck();
  }

  tryInit(): void {
    if (this.isInitialized) return;
    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    if (!this.baseURLs?.api || !combinationKey || !this.globalService?.globalParams) return;

    this.isInitialized = true;
    this.loadScheduleIData();
  }

  loadScheduleIData(): void {
    this.irsF5471ScheduleIData = [];
    this.irsF5471ScheduleIOriginalData = [];

    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    const jcdKey = this.globalService?.globalParams?.jcd_key;
    const jcdKeyVal = jcdKey === '' || jcdKey === undefined ? 250 : jcdKey;
    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;

    const params = {
      action_code: 'pru0jb',
      irs_form_no: 'F5471',
      tax_year: taxYear,
      scenario: scenario,
      combination_key: combinationKey,
      jcd_key: jcdKeyVal
    };

    this.dataLoading = true;

    const baseUrl = this.baseURLs?.api || this.GENERAL_CONFIG?.base_url;
    this.jsonObjectFactory
      .getJSONObj(baseUrl + '/loadJsonObject?action_code=pru0jb', params, this.globalService, null, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (data.callSuccess === '1') {
          const irsF5471ScheduleIDataTemp = data.jsonObject;
          this.processScheduleIData(irsF5471ScheduleIDataTemp);
          this.getGHOSTAmt();
          this.cdr.markForCheck();
        } else {
          this.dataLoading = false;
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar(
              "Sorry you do not have access to do the requested action.",
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          } else {
            this.gtwSnackbarService.openSnackBar(
              'An error has occurred. If you continue to experience this issue please visit the help page to contact support.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          }
        }
      });
  }

  processScheduleIData(irsF5471ScheduleIDataTemp: any[]): void {
    const taxYear = parseInt(
      String(this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || 2018),
      10
    );
    let i = 1;
    let previous_line_no = irsF5471ScheduleIDataTemp[i]?.line_no;

    while (i < irsF5471ScheduleIDataTemp.length) {
      const tempObj: Partial<ScheduleILineItem> = {};
      let j = i;

      while (j < irsF5471ScheduleIDataTemp.length && previous_line_no === irsF5471ScheduleIDataTemp[j].line_no) {
        const parts = irsF5471ScheduleIDataTemp[j].attrib_value?.split('~') || [];
        const objParts = irsF5471ScheduleIDataTemp[j].object_id?.split('~') || [];

        if (parts[0] === 'LINE_NO') {
          if (taxYear < 2018 && irsF5471ScheduleIDataTemp[j].line_no === '6') {
            tempObj.backgroudColor = { background: '#D6D6D6', 'font-weight': 'bold' };
          } else {
            tempObj.backgroudColor = {};
          }
          tempObj.line_no = irsF5471ScheduleIDataTemp[j].line_no;
          tempObj.saveParams = {
            tax_year: objParts[0] || '',
            trans_type_key: objParts[1] || '',
            trans_Details_key: objParts[3] || '',
            occurrence: objParts[4] || '',
            form_key: objParts[6] || ''
          };
        } else if (parts[0] === 'LINE_DESCRIPTION') {
          tempObj.line_Description = parts[3] || '';
        } else if (parts[0] === 'FIR_OVERRIDE_AMT') {
          tempObj.FIR_OVERRIDE_VALUE = {
            VALUE: irsF5471ScheduleIDataTemp[j].VALUE !== undefined ? irsF5471ScheduleIDataTemp[j].VALUE : null,
            line_attrib_key: parts[1] || '',
            attrib_name: parts[0] || '',
            is_editable: irsF5471ScheduleIDataTemp[j].PROPERTY_TYPE === '9',
            is_Display: irsF5471ScheduleIDataTemp[j].PROPERTY_TYPE !== '0',
            column_style: irsF5471ScheduleIDataTemp[j].PROPERTY_TYPE === '0' ? { 'background-color': '#D6D6D6', width: '250px' } : {}
          };
        } else if (parts[0] === 'GHOST_AMT') {
          const propType = irsF5471ScheduleIDataTemp[j].PROPERTY_TYPE;
          const isDisplayGhost = propType === '0' && taxYear >= 2018 ? false : true;
          tempObj.GHOST_AMT = {
            VALUE: irsF5471ScheduleIDataTemp[j].VALUE !== undefined ? parseInt(irsF5471ScheduleIDataTemp[j].VALUE, 10) : null,
            line_attrib_key: parts[1] || '',
            attrib_name: parts[0] || '',
            is_editable: propType === '9',
            is_Display: isDisplayGhost,
            column_style: propType === '0' ? { 'background-color': '#D6D6D6', width: '200px' } : {}
          };
        }
        previous_line_no = irsF5471ScheduleIDataTemp[j].line_no;
        j++;
      }

      if (j < irsF5471ScheduleIDataTemp.length) {
        previous_line_no = irsF5471ScheduleIDataTemp[j].line_no;
      }

      const tempIndex = this.irsF5471ScheduleIData.length;
      if (tempObj.line_no !== undefined) {
        this.irsF5471ScheduleIData[tempIndex] = tempObj as ScheduleILineItem;
      }
      i = j;
    }
  }

  getGHOSTAmt(): void {
    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;

    const params = {
      tax_year: taxYear,
      combination_key: combinationKey,
      scenario: scenario,
      jcd_key: 250
    };

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    const url = baseUrl + '/loadJsonObject?action_code=odx6u9';

    this.jsonObjectFactory
      .getJSONObj(url, params, this.globalService, null, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (data.callSuccess === '1') {
          this.dataLoading = false;
          const responseData = data.jsonObject || [];
          this.GHOSTAmt = responseData.map((r: any) => ({
            LINE_NO: r.LINE_NO,
            ADJ_AMT: r.ADJ_AMT,
            LINE_ATTRIB_KEY: r.LINE_ATTRIB_KEY,
            FORM_KEY: r.FORM_KEY,
            OVERRIDE_Y_N: r.OVERRIDE_Y_N !== undefined ? r.OVERRIDE_Y_N : null,
            TRANS_DETAILS_KEY: r.TRANS_DETAILS_KEY !== undefined ? r.TRANS_DETAILS_KEY : null,
            TRANS_HEADER_KEY: r.TRANS_HEADER_KEY !== undefined ? r.TRANS_HEADER_KEY : null,
            TRANS_TYPE_KEY: r.TRANS_TYPE_KEY != null ? r.TRANS_TYPE_KEY : 36
          }));
          this.loadFlag = true;
          this.restoreGHOSTAmt();
          this.cdr.markForCheck();
        } else {
          this.dataLoading = false;
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar(
              "Sorry you do not have access to do the requested action.",
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          } else {
            this.gtwSnackbarService.openSnackBar(
              'An error has occurred. If you continue to experience this issue please visit the help page to contact support.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          }
        }
      });
  }

  restoreGHOSTAmt(): void {
    if (this.loadFlag) {
      for (let i = 0; i < this.irsF5471ScheduleIData.length; i++) {
        const tempGHOSTObj = _.find(this.GHOSTAmt, {
          LINE_NO: this.irsF5471ScheduleIData[i].line_no
        });
        if (tempGHOSTObj && tempGHOSTObj.OVERRIDE_Y_N === 'N') {
          this.irsF5471ScheduleIData[i].FIR_OVERRIDE_VALUE.VALUE = tempGHOSTObj.ADJ_AMT;
        }
        if (tempGHOSTObj) {
          this.irsF5471ScheduleIData[i].GHOST_AMT.VALUE = tempGHOSTObj.ADJ_AMT;
        }
      }
      this.computeTotal('a');
      this.irsF5471ScheduleIOriginalData = _.cloneDeep(this.irsF5471ScheduleIData);
      this.loadFlag = false;
      this.cdr.markForCheck();
    } else {
      for (let i = 0; i < this.irsF5471ScheduleIData.length; i++) {
        const tempGHOSTObj = _.find(this.GHOSTAmt, {
          LINE_NO: this.irsF5471ScheduleIData[i].line_no
        });
        if (tempGHOSTObj) {
          this.irsF5471ScheduleIData[i].FIR_OVERRIDE_VALUE.VALUE = tempGHOSTObj.ADJ_AMT;
        }
      }
      this.computeTotal('b');
      this.cdr.markForCheck();
    }
  }

  computeTotal(colName: 'a' | 'b'): void {
    let total: string | null = null;
    let i = 0;

    while (i < this.irsF5471ScheduleIData.length && this.irsF5471ScheduleIData[i].line_no !== '6') {
      const line = this.irsF5471ScheduleIData[i];
      const valObj = colName === 'a' ? line.GHOST_AMT : line.FIR_OVERRIDE_VALUE;
      const val = valObj?.VALUE;
      if (val != null && val !== '') {
        if (total != null) {
          total = (parseInt(total, 10) + parseInt(String(val), 10)).toString();
        } else {
          total = String(val);
        }
      }
      i++;
    }

    if (i < this.irsF5471ScheduleIData.length && this.irsF5471ScheduleIData[i].line_no === '6') {
      const line = this.irsF5471ScheduleIData[i];
      const valObj = colName === 'a' ? line.GHOST_AMT : line.FIR_OVERRIDE_VALUE;
      if (valObj) {
        valObj.VALUE = total != null ? parseInt(total, 10) : null;
      }
    }
  }

  onFIROverrideChange(): void {
    this.computeTotal('b');
    this.cdr.markForCheck();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    const updateOverrideFlag: any[] = [];
    const editRow: any[] = [];

    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    const jcdKey = this.globalService?.globalParams?.jcd_key;
    const jcdKeyVal = jcdKey === '' || jcdKey === undefined ? 250 : jcdKey;
    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;

    const scheduleSettings = [{
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: jcdKeyVal
    }];

    for (let i = 0; i < this.irsF5471ScheduleIData.length; i++) {
      const line = this.irsF5471ScheduleIData[i];
      if (line.FIR_OVERRIDE_VALUE?.is_Display) {
        const returnObj1 = [{
          tax_year: taxYear,
          trans_type_key: line.saveParams.trans_type_key,
          combination_key: combinationKey,
          group_obj_key: this.rowData?.GROUP_OBJ_KEY,
          form_key: line.saveParams.form_key,
          line_no: line.line_no,
          occurence: line.saveParams.occurrence === '0' ? null : line.saveParams.occurrence,
          line_attrib_key: line.FIR_OVERRIDE_VALUE.line_attrib_key,
          Attribute_Name: line.FIR_OVERRIDE_VALUE.attrib_name,
          Attribute_Value: line.FIR_OVERRIDE_VALUE.VALUE != null ? line.FIR_OVERRIDE_VALUE.VALUE : 0,
          trans_dtls_key: line.saveParams.trans_Details_key === '0' ? null : line.saveParams.trans_Details_key,
          trans_status: 'A'
        }];
        editRow.push(returnObj1);
      }
    }

    for (let i = 0; i < this.GHOSTAmt.length; i++) {
      const ghost = this.GHOSTAmt[i];
      const lineObj = _.find(this.irsF5471ScheduleIData, { line_no: ghost.LINE_NO });
      if (lineObj) {
        const firVal = lineObj.FIR_OVERRIDE_VALUE.VALUE;
        const ghostVal = ghost.ADJ_AMT;
        const needsUpdate =
          (firVal !== ghostVal && ghost.OVERRIDE_Y_N !== 'Y' && ghost.TRANS_DETAILS_KEY != null) ||
          (firVal === ghostVal && ghost.OVERRIDE_Y_N !== 'N' && ghost.TRANS_DETAILS_KEY != null);
        if (needsUpdate) {
          updateOverrideFlag.push([{
            trans_header_key: ghost.TRANS_HEADER_KEY,
            trans_details_key: ghost.TRANS_DETAILS_KEY,
            trans_type_key: ghost.TRANS_TYPE_KEY,
            line_attrib_key: ghost.LINE_ATTRIB_KEY,
            form_key: ghost.FORM_KEY,
            OVERRIDE_Y_N: ghost.OVERRIDE_Y_N === 'Y' ? 'N' : 'Y'
          }]);
        }
      }
    }

    const scheduleDetails = { addrows: [], editrows: editRow, removerows: [] };
    const message = "Schedule I details are successfully saved/edited";

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    const globalParams = this.globalService?.globalParams || {};

    this.fir5471ScheduleIService
      .saveScheduleI(baseUrl, scheduleSettings, scheduleDetails, globalParams, this.filterParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: any) => {
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          this.crudLoading = false;
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          if (updateOverrideFlag.length > 0) {
            const jsonSettings = [{ combination_key: combinationKey, tax_year: taxYear }];
            this.fir5471ScheduleIService
              .updateFlag(baseUrl, jsonSettings, updateOverrideFlag, globalParams, this.filterParams)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((flagResult: any) => {
                this.crudLoading = false;
                if (flagResult?.errorMessage && flagResult.errorMessage !== 'null') {
                  this.gtwSnackbarService.openSnackBar(flagResult.errorMessage, 'gtw-snackbar--red');
                }
                this.reinitialize();
              });
          } else {
            this.crudLoading = false;
            this.reinitialize();
          }
        }
      });
  }

  reset(): void {
    this.crudLoading = false;
    this.irsF5471ScheduleIData = _.cloneDeep(this.irsF5471ScheduleIOriginalData);
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByLineNo(index: number, item: ScheduleILineItem): string | number {
    return item.line_no;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  reinitialize(): void {
    this.isInitialized = false;
    this.tryInit();
  }
}
